import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Divider from "@/components/Divider";
import Typography from "@/components/Typography";
import LoadingOverlay from "@/components/LoadingOverlay";
import { useUserId } from "../hooks/useUserId";
import {
  useVehiclePlates,
  useSaveVehiclePlate,
  useDeleteVehiclePlate,
} from "../hooks/data/use-settings-data";
import type { VehiclePlate } from "../types/user";
import { Pencil, Trash2, Plus, Check, X } from "lucide-react";
import {
  checkSignatureExists,
  deleteObjectFromS3,
  getPresignedSignatureUploadUrl,
  getSignaturePreviewUrl,
  uploadFileToS3,
} from "../services/api";

type EditState = {
  sk: string;
  plate: string;
  brand: string;
  model: string;
  euroPerKm: string;
};

export function Settings() {
  const userId = useUserId();

  const { data: plates = [], isLoading } = useVehiclePlates(userId);
  const { mutateAsync: savePlate, isPending: isSaving } = useSaveVehiclePlate();
  const { mutateAsync: deletePlate, isPending: isDeleting } =
    useDeleteVehiclePlate();

  const isWorking = isSaving || isDeleting;

  // State for the "add new" form
  const [newPlate, setNewPlate] = useState("");
  const [newBrand, setNewBrand] = useState("");
  const [newModel, setNewModel] = useState("");
  const [newEuroPerKm, setNewEuroPerKm] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);

  // State for inline editing
  const [editing, setEditing] = useState<EditState | null>(null);

  // Validation error visibility
  const [showAddErrors, setShowAddErrors] = useState(false);
  const [showEditErrors, setShowEditErrors] = useState(false);

  // ── Firma ────────────────────────────────────────────────────────────
  const [hasSignature, setHasSignature] = useState<boolean | null>(null); // null = loading
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState<string | null>(null);
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);
  const [isDeletingSignature, setIsDeletingSignature] = useState(false);
  const signatureInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!userId) return;
    checkSignatureExists(userId).then((exists) => {
      setHasSignature(exists);
      if (exists) {
        getSignaturePreviewUrl(userId).then(setSignaturePreviewUrl);
      }
    });
  }, [userId]);

  const handleSignatureUpload = async (file: File) => {
    if (!userId) return;
    setIsUploadingSignature(true);
    try {
      const uploadUrl = await getPresignedSignatureUploadUrl(userId);
      await uploadFileToS3(uploadUrl, file);
      const previewUrl = await getSignaturePreviewUrl(userId);
      setSignaturePreviewUrl(previewUrl);
      setHasSignature(true);
      toast.success("Firma salvata con successo");
    } catch {
      toast.error("Errore durante il caricamento della firma");
    } finally {
      setIsUploadingSignature(false);
    }
  };

  const handleSignatureDelete = async () => {
    if (!userId) return;
    setIsDeletingSignature(true);
    try {
      await deleteObjectFromS3(`signatures/${userId}.png`);
      setHasSignature(false);
      setSignaturePreviewUrl(null);
      toast.success("Firma rimossa");
    } catch {
      toast.error("Errore durante la rimozione della firma");
    } finally {
      setIsDeletingSignature(false);
    }
  };

  const handleAdd = async () => {
    setShowAddErrors(true);
    const trimmed = newPlate.trim().toUpperCase();
    const rate = parseFloat(newEuroPerKm);
    if (!trimmed || !newBrand.trim() || !newModel.trim() || isNaN(rate) || rate <= 0 || !userId) return;
    const sk: VehiclePlate["sk"] = `vehiclePlate#${trimmed}`;
    try {
      await savePlate({
        pk: userId,
        sk,
        plate: trimmed,
        brand: newBrand.trim(),
        model: newModel.trim(),
        euroPerKm: rate,
      });
      toast.success("Veicolo aggiunto con successo");
      setNewPlate("");
      setNewBrand("");
      setNewModel("");
      setNewEuroPerKm("");
      setShowAddErrors(false);
      setShowAddForm(false);
    } catch {
      toast.error("Errore durante il salvataggio");
    }
  };

  const handleSaveEdit = async () => {
    if (!editing || !userId) return;
    setShowEditErrors(true);
    const rate = parseFloat(editing.euroPerKm);
    if (!editing.brand.trim() || !editing.model.trim() || !editing.plate.trim() || isNaN(rate) || rate <= 0) return;
    try {
      const existing = plates.find((p) => p.sk === editing.sk);
      await savePlate({
        pk: userId,
        sk: editing.sk as VehiclePlate["sk"],
        plate: editing.plate.trim().toUpperCase(),
        brand: editing.brand.trim(),
        model: editing.model.trim(),
        euroPerKm: rate,
        lastUpdate: existing?.lastUpdate,
      });
      toast.success("Veicolo aggiornato");
      setShowEditErrors(false);
      setEditing(null);
    } catch {
      toast.error("Errore durante l'aggiornamento");
    }
  };

  const handleDelete = async (plate: VehiclePlate) => {
    try {
      await deletePlate({ key: { pk: plate.pk, sk: plate.sk } });
      toast.success("Targa eliminata");
    } catch {
      toast.error("Errore durante l'eliminazione");
    }
  };

  return (
    <div className="Settings">
      {(isLoading) && <LoadingOverlay />}

      <section className="Settings__section">
        <div className="Settings__section-header">
          <Typography variant="h5" className="bold">
            Targhe veicoli
          </Typography>
          <Typography variant="body2">
            Registra le targhe dei tuoi veicoli per i rimborsi kilometrici.
          </Typography>
          <Divider />
        </div>

        <ul className="Settings__plates-list">
          {plates.map((p) =>
            editing?.sk === p.sk ? (
              <li key={p.sk} className="Settings__plates-item Settings__plates-item--editing">
                <Input
                  autoFocus
                  value={editing.brand}
                  onChange={(e) =>
                    setEditing((prev) => prev && { ...prev, brand: e.target.value })
                  }
                  placeholder="Marca"
                  className="Settings__plates-input"
                />
                {showEditErrors && !editing.brand.trim() && (
                  <span className="Settings__field-error">Inserisci la marca</span>
                )}
                <Input
                  value={editing.model}
                  onChange={(e) =>
                    setEditing((prev) => prev && { ...prev, model: e.target.value })
                  }
                  placeholder="Modello"
                  className="Settings__plates-input"
                />
                {showEditErrors && !editing.model.trim() && (
                  <span className="Settings__field-error">Inserisci il modello</span>
                )}
                <Input
                  value={editing.plate}
                  onChange={(e) =>
                    setEditing((prev) => prev && { ...prev, plate: e.target.value })
                  }
                  placeholder="Targa"
                  className="Settings__plates-input"
                />
                {showEditErrors && !editing.plate.trim() && (
                  <span className="Settings__field-error">Inserisci la targa</span>
                )}
                <Input
                  value={editing.euroPerKm}
                  type="number"
                  step="any"
                  min={0.01}
                  onChange={(e) =>
                    setEditing((prev) => prev && { ...prev, euroPerKm: e.target.value })
                  }
                  placeholder="€/km (es. 0.25)"
                  className="Settings__plates-input"
                />
                {showEditErrors && (isNaN(parseFloat(editing.euroPerKm)) || parseFloat(editing.euroPerKm) <= 0) && (
                  <span className="Settings__field-error">Inserisci una tariffa valida (€/km)</span>
                )}
                <div className="Settings__plates-actions">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={handleSaveEdit}
                    disabled={isWorking}
                    aria-label="Conferma"
                  >
                    <Check size={16} />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => { setEditing(null); setShowEditErrors(false); }}
                    aria-label="Annulla"
                  >
                    <X size={16} />
                  </Button>
                </div>
              </li>
            ) : (
              <li key={p.sk} className="Settings__plates-item">
                <div className="Settings__plates-info">
                  <Typography variant="body1" className="bold">
                    {p.brand} {p.model}
                  </Typography>
                  <Typography variant="body2">{p.plate}</Typography>
                  <Typography variant="body2">{p.euroPerKm ?? "—"} €/km</Typography>
                </div>
                <div className="Settings__plates-actions">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() =>
                      setEditing({ sk: p.sk, plate: p.plate, brand: p.brand, model: p.model, euroPerKm: String(p.euroPerKm ?? "") })
                    }
                    disabled={isWorking}
                    aria-label="Modifica"
                  >
                    <Pencil size={16} />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleDelete(p)}
                    disabled={isWorking}
                    aria-label="Elimina"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </li>
            ),
          )}

          {plates.length === 0 && !isLoading && (
            <li>
              <Typography variant="body2">Nessuna targa registrata.</Typography>
            </li>
          )}
        </ul>

        {showAddForm ? (
          <div className="Settings__add-form">
            <Input
              autoFocus
              placeholder="Marca (es. Fiat)"
              value={newBrand}
              onChange={(e) => setNewBrand(e.target.value)}
            />
            {showAddErrors && !newBrand.trim() && (
              <span className="Settings__field-error">Inserisci la marca</span>
            )}
            <Input
              placeholder="Modello (es. Panda)"
              value={newModel}
              onChange={(e) => setNewModel(e.target.value)}
            />
            {showAddErrors && !newModel.trim() && (
              <span className="Settings__field-error">Inserisci il modello</span>
            )}
            <Input
              placeholder="Targa (es. AB123CD)"
              value={newPlate}
              onChange={(e) => setNewPlate(e.target.value.toUpperCase())}
            />
            {showAddErrors && !newPlate.trim() && (
              <span className="Settings__field-error">Inserisci la targa</span>
            )}
            <Input
              placeholder="€ per km (es. 0.25)"
              type="number"
              step="any"
              min={0.01}
              value={newEuroPerKm}
              onChange={(e) => setNewEuroPerKm(e.target.value)}
            />
            {showAddErrors && (isNaN(parseFloat(newEuroPerKm)) || parseFloat(newEuroPerKm) <= 0) && (
              <span className="Settings__field-error">Inserisci una tariffa valida (€/km)</span>
            )}
            <div className="Settings__add-form-buttons">
              <Button variant="ghost" onClick={() => { setShowAddForm(false); setNewPlate(""); setNewBrand(""); setNewModel(""); setNewEuroPerKm(""); setShowAddErrors(false); }}>
                Annulla
              </Button>
              <Button onClick={handleAdd} disabled={isWorking}>
                Salva
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="ghost"
            onClick={() => setShowAddForm(true)}
            className="Settings__add-button"
          >
            <Plus size={16} />
            Aggiungi targa
          </Button>
        )}
      </section>

      {/* ── Sezione Firma ─────────────────────────────────────────────── */}
      <section className="Settings__section">
        <div className="Settings__section-header">
          <Typography variant="h5" className="bold">
            Firma
          </Typography>
          <Typography variant="body2">
            Carica l'immagine della tua firma. Verrà allegata automaticamente alla nota spese.
          </Typography>
          <Divider />
        </div>

        {hasSignature === null && (
          <Typography variant="body2">Caricamento...</Typography>
        )}

        {hasSignature === true && signaturePreviewUrl && (
          <div className="Settings__signature-preview-container">
            <img
              src={signaturePreviewUrl}
              alt="Firma"
              className="Settings__signature-preview"
            />
            <div className="Settings__signature-actions">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => signatureInputRef.current?.click()}
                disabled={isUploadingSignature || isDeletingSignature}
              >
                Sostituisci
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignatureDelete}
                disabled={isUploadingSignature || isDeletingSignature}
              >
                <Trash2 size={16} />
                Rimuovi
              </Button>
            </div>
          </div>
        )}

        {hasSignature === false && (
          <div className="Settings__signature-upload">
            <Button
              variant="ghost"
              onClick={() => signatureInputRef.current?.click()}
              disabled={isUploadingSignature}
              className="Settings__add-button"
            >
              <Plus size={16} />
              {isUploadingSignature ? "Caricamento..." : "Carica firma"}
            </Button>
          </div>
        )}

        <input
          ref={signatureInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="Settings__signature-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleSignatureUpload(file);
            e.target.value = "";
          }}
        />
      </section>
    </div>
  );
}
